import {
  buildQuantityOptions,
  isLineItemPurchasable,
} from 'components/ProductDetails';
import { notifyError } from 'config/setupBugsnag';
import getConfig from 'next/config';
import {
  Cart,
  CustomCaseLineItem,
  IOrder,
  JsonApiDocument,
  ProductDiscounts,
  PromotionListClient,
  PromotionListResponse,
} from 'types';
import RestUtility from '../../restUtility';
import { cartEndPoint, couponEndPoint } from '../../restUtility/restEndpoints';

export default class CartService {
  private restUtility: RestUtility;
  private cartQueryFields: string;

  constructor() {
    this.restUtility = new RestUtility();
    this.cartQueryFields =
      'line_items,variants,variants.images,promotions&fields[image]=thumbnail_url,original_url';
  }

  async createCart() {
    try {
      const response = await this.restUtility.post<IOrder>(
        `${cartEndPoint}?include=${this.cartQueryFields}`,
        {}
      );

      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: createCart', error);
      throw error;
    }
  }

  async addLineitem(
    variantId: string,
    description?: string | null,
    code?: string | null,
    quantity?: number,
    isRedeemPointSelected?: boolean,
    projectId?: string,
    options?: {
      subscribe?: boolean;
      subscription_frequency_id?: string;
    }
  ) {
    try {
      const params = quantity
        ? {
            variant_id: variantId,
            description: description,
            code: code,
            quantity: quantity,
            reedeem: isRedeemPointSelected,
            dh_project_id: projectId,
            options,
          }
        : { variant_id: variantId };
      const couponCodeParameter = code ? `&coupon_code=${code}` : '';
      const response = await this.restUtility.post<IOrder>(
        `${cartEndPoint}/add_item?include=${this.cartQueryFields}${couponCodeParameter}`,
        params
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: addLineitem', error);
      throw error;
    }
  }

  async addLineitems(lineItems: CustomCaseLineItem[], couponCode?: string) {
    try {
      const params = { line_items: lineItems, coupon_code: couponCode };
      const response = await this.restUtility.post<IOrder>(
        `${cartEndPoint}/add_custom_clamshells?include=${this.cartQueryFields}`,
        params
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: addLineitems', error);
      throw error;
    }
  }
  async removeLineitem(lineItemId: string) {
    try {
      const response = await this.restUtility.delete<IOrder>(
        `${cartEndPoint}/remove_line_item/${lineItemId}?include=${this.cartQueryFields}`
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: removeLineitem', error);
      throw error;
    }
  }

  async emptyCart() {
    try {
      const response = await this.restUtility.patch<IOrder>(
        `${cartEndPoint}/empty?include=${this.cartQueryFields}`
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: emptyCart', error);
      throw error;
    }
  }

  async setLineItemQuantity(
    variantId: string,
    quantity: number,
    isRewardPointSelected: boolean
  ) {
    try {
      const response = await this.restUtility.patch<IOrder>(
        `${cartEndPoint}/set_quantity?include=${this.cartQueryFields}`,
        {
          line_item_id: variantId,
          quantity: quantity,
          reedeem: isRewardPointSelected,
        }
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: setLineItemQuantity', error);
      throw error;
    }
  }

  async proceedToCheckout() {
    try {
      const response = await this.restUtility.post<IOrder>(
        `${cartEndPoint}/proceed_to_checkout?include=billing_address,shipping_address,${this.cartQueryFields}`,
        {}
      );

      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: proceedToCheckout', error);
      throw error;
    }
  }

  async getPromotionList() {
    try {
      const response = await this.restUtility.get<PromotionListResponse>(
        `${couponEndPoint}`
      );
      return this.buildPromotions(response);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async removeCouponCode(couponCode: string) {
    try {
      const response = await this.restUtility.delete<IOrder>(
        `${cartEndPoint}/remove_coupon_code/${couponCode}`
      );

      return response;
    } catch (error) {
      notifyError('ApiError:: removeCouponCode', error);
      // throw error;
    }
  }

  async applyCouponCode(couponCode: string) {
    try {
      const response = await this.restUtility.patch<IOrder>(
        `${cartEndPoint}/apply_coupon_code`,
        { coupon_code: couponCode }
      );
      console.log(`success applyCouponCode for ${couponCode}`);
      return response;
    } catch (error) {
      notifyError('ApiError:: applyCouponCode', error);
      // throw error;
    }
  }

  async assignTemplateToOrder(templateCode: string) {
    try {
      const response = await this.restUtility.put<IOrder>(
        `${cartEndPoint}/add_design?include=${this.cartQueryFields}`,
        { order: { design_id: templateCode } }
      );
      return this.buildCartForClient(response);
    } catch (error) {
      notifyError('ApiError:: assignTemplateToOrder', error);
      throw error;
    }
  }

  isRewardsPointApplied(lineItem: JsonApiDocument) {
    return lineItem.attributes.checked && lineItem.attributes.redeem_points > 0;
  }

  buildCartForClient(response: IOrder) {
    const { publicRuntimeConfig } = getConfig();
    const cart: Cart = {
      state: '',
      lineItems: [],
      total: 0,
      displayTotal: '',
      displaySubTotal: '',
      displayLineItemsTotalWithDiscounts: '',
      displayLineItemsTotalWithoutDiscount: '',
      lineItemSavings: '',
      shipmentSavings: '',
      orderSavings: '',
      itemCount: 0,
      taxTotal: '',
      displayTaxTotal: '',
      withholdingTaxTotal: '',
      shipTotal: '',
      displayShipTotal: '',
      isBulkDiscountEligible: false,
      displayBulkDiscountTotal: '',
      bulkDiscountTotal: 0,
      billToAddressId: '',
      shipToAddressId: '',
      checkoutStates: [],
      currency: '',
      promotions: [],
      email: '',
      isCustomClamShellOrder: false,
      totalRedeemPoints: '',
    };
    const lineItems = response.included?.filter(
      item => item.type === 'line_item'
    );
    const variants = response.included?.filter(item => item.type === 'variant');
    const images = response.included?.filter(item => item.type === 'image');
    const appliedPromotions = response.included?.filter(
      item => item.type === 'promotion'
    );

    const cartData = response.data;
    cart.state = cartData.attributes.state;
    cart.total = cartData.attributes.total;
    cart.displayTotal = cartData.attributes.display_total;
    cart.displaySubTotal = cartData.attributes.display_item_total;
    cart.displayLineItemsTotalWithDiscounts =
      cartData.attributes.display_line_items_total_after_discounts;
    cart.displayLineItemsTotalWithoutDiscount =
      cartData.attributes.display_line_items_total_before_discounts;
    cart.itemCount = cartData.attributes.item_count;
    cart.taxTotal = cartData.attributes.tax_amount;
    cart.displayTaxTotal = cartData.attributes.display_tax_amount;
    cart.withholdingTaxTotal = cartData.attributes.withholding_tax_amount ?? '';
    cart.shipTotal = cartData.attributes.ship_total;
    cart.displayShipTotal = cartData.attributes.display_ship_total;
    cart.isBulkDiscountEligible = cartData.attributes.is_bulk_discount_eligible;
    cart.displayBulkDiscountTotal =
      cartData.attributes.display_bulk_discount_amount;
    cart.bulkDiscountTotal = cartData.attributes.bulk_discount_amount;
    cart.shipToAddressId = cartData.relationships.shipping_address?.data?.id;
    cart.billToAddressId = cartData.relationships.billing_address?.data?.id;
    cart.checkoutStates = cartData.attributes.checkout_states;
    cart.currency = cartData.attributes.currency;
    cart.email = cartData.attributes.email;
    cart.clamshellTemplateId = cartData.attributes.design_id;
    cart.isCustomClamShellOrder = cartData.attributes.is_custom_clamshell_order;
    cart.lineItemSavings = cartData.attributes.display_line_items_savings;
    cart.shipmentSavings = cartData.attributes.display_shipment_savings;
    cart.orderSavings = cartData.attributes.display_order_savings;
    cart.totalRedeemPoints = cartData.attributes.total_redeem_points;

    if (appliedPromotions && appliedPromotions?.length > 0) {
      appliedPromotions.forEach(promo => {
        cart.promotions.push({
          promoCode: promo.attributes.code,
          promoTotal: promo.attributes.display_amount,
        });
      });
    }

    if (lineItems) {
      lineItems.forEach(lineItem => {
        const lineItemOriginalVariant = variants?.filter(
          variant => variant.id === lineItem.relationships.variant.data.id
        )[0];

        const skuListWithQuantityBasedPricing =
          publicRuntimeConfig.SKU_LIST_WITH_QUANTITY_BASED_PRICING.split(',');

        const lineItemImage = images?.filter(image => {
          return (
            image.id ===
            lineItemOriginalVariant?.relationships.images.data[0]?.id
          );
        })[0];

        const isProductPriceChanged =
          parseFloat(lineItem.attributes.price).toFixed(2) !==
            parseFloat(lineItemOriginalVariant?.attributes.price).toFixed(2) &&
          !this.isRewardsPointApplied(lineItem) &&
          !skuListWithQuantityBasedPricing.includes(
            lineItemOriginalVariant?.attributes.sku.toString()
          );

        const lineItemImageUrl = this.getThumbnailUrl(lineItemImage);

        const customProductQuantityLimit =
          lineItemOriginalVariant?.attributes.quantity_limit;

        cart.lineItems.push({
          id: lineItem.id,
          name: lineItem.attributes.name,
          slug: lineItem.attributes.slug,
          quantity: lineItem.attributes.quantity,
          quantityLimit: buildQuantityOptions(customProductQuantityLimit),
          total: lineItem.attributes.total,
          displayTotal: lineItem.attributes.display_total_after_bulk_discount,
          displayTotalWithoutDiscount:
            lineItem.attributes.display_total_without_adjustments,

          price: lineItem.attributes.price,
          displayPrice: lineItem.attributes.display_price,
          inStock: isLineItemPurchasable(
            lineItemOriginalVariant?.attributes.backorderable,
            lineItemOriginalVariant?.attributes.in_stock,
            lineItemOriginalVariant?.attributes.quantity_limit
          ),
          isOriginalProductPriceChanged: isProductPriceChanged,
          imageUrl: lineItemImageUrl,
          isHavingPromoCodeAdjustments:
            lineItem.attributes.is_having_promo_code_adjustments,

          isHavingSubsriptionAdjustments:
            lineItem.attributes.is_having_subscription_adjustments,
          isHavingAnyAdjustments:
            lineItem.attributes.is_having_subscription_adjustments ||
            lineItem.attributes.is_having_bulk_discount_adjustments ||
            lineItem.attributes.is_having_promo_code_adjustments ||
            lineItem.attributes.is_having_dso_discount_adjustments,
          discountPercentage: lineItem.attributes.discount_percentage,
          displayDiscountAmount: lineItem.attributes.display_discount_amount,
          couponCode: lineItem.attributes.coupon_code,
          isHavingBulkDiscountAdjustments:
            lineItem.attributes.is_having_bulk_discount_adjustments,
          isHavingBulkDiscount: lineItem.attributes.is_having_bulk_discount,
          bulkDiscountPromoTitle: lineItem.attributes.bulk_discount_promo_title,
          bulkDiscountPromoDescription:
            lineItem.attributes.bulk_discount_promo_description,
          isHavingDSODiscount: lineItem.attributes.is_having_dso_discount,
          isHavingDSODiscountAdjustments:
            lineItem.attributes.is_having_dso_discount_adjustments,
          dsoDiscountPromoTitle: lineItem.attributes.dso_discount_promo_title,
          dsoDiscountPromoDescription:
            lineItem.attributes.dso_discount_promo_description,
          sku: lineItemOriginalVariant?.attributes.sku,
          isRewardPointsRedeemed: lineItem.attributes.checked,
          redeemedPoints: lineItem.attributes.redeem_points,
          discontinueOn: lineItemOriginalVariant?.attributes.discontinue_on,
          isCustomizable: lineItemOriginalVariant?.attributes.is_customizable,
          dhProjectId: lineItem.attributes.dh_project_id,
          displayTotalAfterSubscriptionDiscounts:
            lineItem.attributes.display_total_after_subscription_discounts,
          isSubscribed: lineItem.attributes.is_subscribed,
          subscriptionFrequencyId:
            lineItem.attributes.subscription_frequency_id,
          subscriptionTitle: lineItem.attributes.subscription_title,
        });
      });
    }
    return cart;
  }

  buildPromotions(response: PromotionListResponse): PromotionListClient {
    const promotionListResponse = response.data;
    const promotionListClient: PromotionListClient = {
      productsList: [],
      flashSale: { message: '', expiresAtUTC: '' },
      redeemProductList: [],
    };

    const promotionalProducts: ProductDiscounts[] =
      promotionListResponse.promotional_products;
    const flashSale = promotionListResponse.flashsale;
    const redeemProducts = promotionListResponse.redeem_products;

    if (promotionalProducts) {
      promotionalProducts.forEach(product => {
        const mappedDiscounts = product.discounts.map(discount => {
          if (discount.discount_type === 'bulk_discount') {
            return {
              discountType: discount.discount_type,
              title: discount.title,
              description: discount.description,
            };
          }

          if (discount.discount_type === 'dso_discount') {
            return {
              discountType: discount.discount_type,
              title: discount.title,
              description: discount.description,
              discountPercentage: discount.discount_percentage,
            };
          }

          return {
            discountType: 'coupon' as const,
            highlighterText: discount.highlighter_text,
            code: discount.code,
            matchPolicy: discount.match_policy,
            description: discount.description,
            name: discount.name,
            type: discount.type,
            isFlashSale: discount.is_flashsale,
            usage: discount.usage,
            expiresAt: discount.expires_at_utc,
            originalPrice: discount.original_price,
            discountPrice: discount.discount_price,
            stackable: discount.stackable,
          };
        });

        const clientProduct = {
          sku: product.sku,
          discounts: mappedDiscounts,
        };

        promotionListClient.productsList.push(clientProduct);
      });
    }

    if (flashSale) {
      promotionListClient.flashSale = {
        message: flashSale.message,
        expiresAtUTC: flashSale.expires_at_utc,
      };
    }

    if (redeemProducts) {
      redeemProducts.forEach(redeemProduct => {
        promotionListClient.redeemProductList.push({
          id: redeemProduct.id,
          sku: redeemProduct.sku,
          points: redeemProduct.points,
          price: redeemProduct.redeem_price,
          displayPriceWithoutPoints: redeemProduct.display_price_with_points,
          displayPriceWithPoints:
            redeemProduct.amount_payable_after_redeem_points,
        });
      });
    }
    return promotionListClient;
  }

  getThumbnailUrl(productImage: JsonApiDocument | undefined) {
    const { publicRuntimeConfig } = getConfig();
    return productImage
      ? `${publicRuntimeConfig.IMAGE_HOST}${productImage.attributes.thumbnail_url}`
      : '/images/noimage.jpeg';
  }
}
